import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, PackageSearch, Gift, ShoppingBag, Menu, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Tech Value brand lockup — uses the gold/silver gear + globe logo image.
 */
export function TechValueLockup({ size = "md", tagline = true, dark = false, mono = false }) {
  const logoSize =
    size === "xl" ? "w-24 h-24 sm:w-28 sm:h-28" :
    size === "lg" ? "w-16 h-16" :
    "w-11 h-11";
  const titleCls =
    size === "xl" ? "text-4xl sm:text-5xl" :
    size === "lg" ? "text-2xl sm:text-3xl" :
    "text-xl";
  const taglineCls = size === "xl" ? "text-sm" : size === "lg" ? "text-xs" : "text-[10px]";
  return (
    <div className="inline-flex items-center gap-3">
      <img
        src="/tech-value-logo.png"
        alt="Tech Value"
        className={`${logoSize} object-contain drop-shadow-sm shrink-0`}
        data-testid="tv-logo"
      />
      <div className="leading-none">
        <div className={`font-brand font-extrabold tracking-tighter ${titleCls} ${dark || mono ? "text-white" : "text-foreground"}`}>
          Tech <span className="bg-gradient-to-r from-amber-500 via-yellow-500 to-slate-500 bg-clip-text text-transparent">Value</span>
        </div>
        {tagline && (
          <div className={`font-mono uppercase tracking-[0.22em] mt-1 ${taglineCls} ${dark ? "text-slate-300" : "text-muted-foreground"}`}>
            Buyback · Sell · Repair
          </div>
        )}
      </div>
    </div>
  );
}

// Backwards-compat alias
export const TechWorldLockup = TechValueLockup;

export default function HeaderNav() {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 tv-glass border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <Link to="/" className="group" data-testid="brand-logo">
          <TechValueLockup size="md" tagline />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold">
          <a href="/#brands" className="hover:text-primary transition-colors" data-testid="nav-brands">Sell Phone</a>
          <Link to="/shop" className="hover:text-primary transition-colors flex items-center gap-1.5" data-testid="nav-shop">
            <ShoppingBag className="w-4 h-4" /> Buy Refurbished
          </Link>
          <Link to="/repair" className="hover:text-primary transition-colors flex items-center gap-1.5" data-testid="nav-repair">
            <Wrench className="w-4 h-4" /> Book Repair
          </Link>
          <a href="/#how-it-works" className="hover:text-primary transition-colors" data-testid="nav-how">How it works</a>
          <Link to="/track" className="hover:text-primary transition-colors flex items-center gap-1.5" data-testid="nav-track">
            <PackageSearch className="w-4 h-4" /> Track Order
          </Link>
          <Link to="/credits" className="hover:text-primary transition-colors flex items-center gap-1.5" data-testid="nav-credits">
            <Gift className="w-4 h-4" /> My Credits
          </Link>
          <a href="/#store" className="hover:text-primary transition-colors" data-testid="nav-store">Store</a>
        </nav>

        <div className="flex items-center gap-2">
          <div className="lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => (window.location.href = "/#brands")}>Sell Phone</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/shop")}>Buy Refurbished</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/repair")}>Book Repair</DropdownMenuItem>
                <DropdownMenuItem onClick={() => (window.location.href = "/#how-it-works")}>How it works</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/track")}>Track Order</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/credits")}>My Credits</DropdownMenuItem>
                <DropdownMenuItem onClick={() => (window.location.href = "/#store")}>Store</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/login")}
            className="text-muted-foreground hidden sm:inline-flex"
            data-testid="admin-login-nav-btn"
          >
            <LogIn className="w-4 h-4 mr-1.5" /> Admin
          </Button>
          <Button
            size="sm"
            className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:opacity-90 text-white shadow-lg shadow-amber-500/30 font-bold"
            onClick={() => {
              document.getElementById("brands")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="header-sell-btn"
          >
            Sell Now
          </Button>
        </div>
      </div>
    </header>
  );
}
